// Visual animado do hero da landing do minipay — SVG puro (sem dependências),
// combinando os três temas do produto: conversa de WhatsApp (bolhas de chat),
// financeiro (moeda + mini gráfico de saldo) e IA (anel de nós pulsantes +
// sparkles), todos orbitando um "celular" central. Mesma técnica de <style>
// com @keyframes já usada no AssistantRobot.
const ACCENT = "#4ADF83";
const DARK = "#0F3D24";
const MINT = "#EAF7EF";
const LEAF = "#1FAA59";
const GOLD = "#F5B94A";

const RING_NODES = [
  { cx: 275, cy: 175, kind: "chat", delay: 0 },
  { cx: 217.5, cy: 274.6, kind: "coin", delay: 0.4 },
  { cx: 102.5, cy: 274.6, kind: "ai", delay: 0.8 },
  { cx: 45, cy: 175, kind: "chat", delay: 1.2 },
  { cx: 102.5, cy: 75.4, kind: "coin", delay: 1.6 },
  { cx: 217.5, cy: 75.4, kind: "ai", delay: 2 },
];

function ChatBadge({ cx, cy }) {
  return (
    <g transform={`translate(${cx}, ${cy})`}>
      <circle r="15" fill={ACCENT} />
      <path
        d="M-7 -4a7 7 0 1 1 3 12l-5 2 1.5-4.6A7 7 0 0 1 -7 -4z"
        fill="#ffffff"
      />
    </g>
  );
}

function CoinBadge({ cx, cy }) {
  return (
    <g transform={`translate(${cx}, ${cy})`}>
      <circle r="15" fill={GOLD} />
      <text
        y="4"
        textAnchor="middle"
        fontSize="10"
        fontWeight="800"
        fontFamily="Arial, sans-serif"
        fill="#5a3a06"
      >
        R$
      </text>
    </g>
  );
}

function AiBadge({ cx, cy }) {
  return (
    <g transform={`translate(${cx}, ${cy})`}>
      <circle r="15" fill={DARK} />
      <path
        d="M0 -7l1.6 5.4L7 0l-5.4 1.6L0 7l-1.6-5.4L-7 0l5.4-1.6z"
        fill={ACCENT}
      />
    </g>
  );
}

const BADGES = { chat: ChatBadge, coin: CoinBadge, ai: AiBadge };

export default function FinanceAiVisual({ size = 340 }) {
  return (
    <>
      <style>{`
        @keyframes fav-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes fav-pulse { 0%,100% { opacity: .3; transform: scale(1); } 50% { opacity: .6; transform: scale(1.15); } }
        @keyframes fav-orbit { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(6deg); } }
        @keyframes fav-node { 0%,100% { opacity: .4; transform: scale(0.92); } 50% { opacity: 1; transform: scale(1); } }
        @keyframes fav-msg-in {
          0% { opacity: 0; transform: translateY(6px); }
          8% { opacity: 1; transform: translateY(0); }
          35% { opacity: 1; transform: translateY(0); }
          45%,100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes fav-typing-bubble {
          0%,38% { opacity: 0; }
          46%,62% { opacity: 1; }
          70%,100% { opacity: 0; }
        }
        @keyframes fav-typing-dot { 0%,60%,100% { transform: translateY(0); opacity: .4; } 30% { transform: translateY(-3px); opacity: 1; } }
        @keyframes fav-paid-in {
          0%,65% { opacity: 0; transform: translateY(6px); }
          73%,92% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes fav-bar-grow { 0%,100% { transform: scaleY(.55); } 50% { transform: scaleY(1); } }
        @keyframes fav-coin-float { 0%,100% { transform: translateY(0) rotate(-6deg); } 50% { transform: translateY(-6px) rotate(6deg); } }
        @keyframes fav-spark { 0%,100% { opacity: .2; transform: scale(.8); } 50% { opacity: 1; transform: scale(1.15); } }

        .fav-outer { animation: fav-float 4.5s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .fav-glow-1 { animation: fav-pulse 4.2s ease-in-out infinite; }
        .fav-glow-2 { animation: fav-pulse 4.8s ease-in-out 1s infinite; }
        .fav-ring { animation: fav-orbit 18s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .fav-node { animation: fav-node 2.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .fav-msg { animation: fav-msg-in 8s ease-in-out infinite; }
        .fav-typing { animation: fav-typing-bubble 8s ease-in-out infinite; }
        .fav-dot-1, .fav-dot-2, .fav-dot-3 { animation: fav-typing-dot 1.1s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .fav-dot-2 { animation-delay: .15s; }
        .fav-dot-3 { animation-delay: .3s; }
        .fav-paid { animation: fav-paid-in 8s ease-in-out infinite; }
        .fav-bar { animation: fav-bar-grow 2.2s ease-in-out infinite; transform-box: fill-box; transform-origin: bottom; }
        .fav-coin-badge { animation: fav-coin-float 3.2s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .fav-spark { animation: fav-spark 2.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
      `}</style>
      <svg
        width={size}
        height={(size * 340) / 320}
        viewBox="0 0 320 340"
        fill="none"
        style={{ overflow: "visible", flexShrink: 0 }}
      >
        <defs>
          <filter id="fav-blur" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="20" />
          </filter>
          <filter id="fav-shadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#0B2818" floodOpacity="0.18" />
          </filter>
        </defs>

        <g className="fav-outer">
          <circle className="fav-glow-1" cx="160" cy="175" r="118" fill={ACCENT} opacity="0.3" filter="url(#fav-blur)" />
          <circle className="fav-glow-2" cx="195" cy="150" r="80" fill={LEAF} opacity="0.2" filter="url(#fav-blur)" />

          <g className="fav-ring">
            {RING_NODES.map((n, i) => (
              <line
                key={`spoke-${i}`}
                x1={n.cx}
                y1={n.cy}
                x2="160"
                y2="175"
                stroke={DARK}
                strokeOpacity="0.1"
                strokeWidth="1.5"
              />
            ))}
            {RING_NODES.map((n, i) => {
              const Badge = BADGES[n.kind];
              return (
                <g key={`node-${i}`} className="fav-node" style={{ animationDelay: `${n.delay}s` }}>
                  <Badge cx={n.cx} cy={n.cy} />
                </g>
              );
            })}
          </g>

          {/* sparkles soltos */}
          <path className="fav-spark" style={{ animationDelay: "0s" }} d="M258 44l2.2 5.8 5.8 2.2-5.8 2.2-2.2 5.8-2.2-5.8-5.8-2.2 5.8-2.2z" fill={ACCENT} />
          <path className="fav-spark" style={{ animationDelay: "1s" }} d="M40 110l1.8 4.8 4.8 1.8-4.8 1.8-1.8 4.8-1.8-4.8-4.8-1.8 4.8-1.8z" fill={LEAF} />
          <path className="fav-spark" style={{ animationDelay: "1.8s" }} d="M258 300l1.8 4.8 4.8 1.8-4.8 1.8-1.8 4.8-1.8-4.8-4.8-1.8 4.8-1.8z" fill={ACCENT} />

          {/* celular central */}
          <g filter="url(#fav-shadow)">
            <rect x="108" y="68" width="104" height="196" rx="24" fill={DARK} />
            <rect x="148" y="76" width="24" height="4" rx="2" fill="#ffffff" opacity="0.35" />
            <rect x="116" y="84" width="88" height="162" rx="16" fill={MINT} />

            {/* header do chat */}
            <circle cx="132" cy="100" r="7" fill={ACCENT} />
            <rect x="144" y="96" width="32" height="4" rx="2" fill={DARK} opacity="0.55" />
            <rect x="144" y="104" width="20" height="3" rx="1.5" fill={DARK} opacity="0.3" />
            <rect x="116" y="114" width="88" height="1" fill={DARK} opacity="0.08" />

            {/* mensagem recebida */}
            <g className="fav-msg">
              <rect x="122" y="120" width="60" height="30" rx="10" fill="#ffffff" stroke={DARK} strokeOpacity="0.08" />
              <text x="130" y="132" fontSize="7" fontFamily="Arial, sans-serif" fill={DARK} opacity="0.55">Aluguel</text>
              <text x="130" y="143" fontSize="8" fontFamily="Arial, sans-serif" fontWeight="700" fill={DARK}>R$ 450</text>
            </g>

            {/* indicador de digitando (IA processando) */}
            <g className="fav-typing">
              <rect x="122" y="156" width="44" height="22" rx="11" fill="#ffffff" stroke={DARK} strokeOpacity="0.08" />
              <circle className="fav-dot-1" cx="133" cy="167" r="2.2" fill={DARK} opacity="0.5" />
              <circle className="fav-dot-2" cx="141" cy="167" r="2.2" fill={DARK} opacity="0.5" />
              <circle className="fav-dot-3" cx="149" cy="167" r="2.2" fill={DARK} opacity="0.5" />
            </g>

            {/* confirmação de pagamento */}
            <g className="fav-paid">
              <rect x="148" y="190" width="56" height="28" rx="10" fill={ACCENT} />
              <text x="156" y="208" fontSize="9" fontFamily="Arial, sans-serif" fontWeight="700" fill="#0a2414">Pago ✓</text>
            </g>
          </g>

          {/* mini gráfico de saldo flutuante */}
          <g filter="url(#fav-shadow)">
            <rect x="186" y="196" width="74" height="58" rx="14" fill="#ffffff" stroke={DARK} strokeOpacity="0.08" />
            <text x="196" y="210" fontSize="7" fontFamily="Arial, sans-serif" fontWeight="700" letterSpacing="0.05em" fill={DARK} opacity="0.45">SALDO</text>
            <rect className="fav-bar" style={{ animationDelay: "0s" }} x="196" y="238" width="6" height="10" rx="2" fill={LEAF} />
            <rect className="fav-bar" style={{ animationDelay: ".25s" }} x="206" y="230" width="6" height="18" rx="2" fill={ACCENT} />
            <rect className="fav-bar" style={{ animationDelay: ".5s" }} x="216" y="234" width="6" height="14" rx="2" fill={LEAF} />
            <rect className="fav-bar" style={{ animationDelay: ".75s" }} x="226" y="224" width="6" height="24" rx="2" fill={ACCENT} />
          </g>

          {/* moeda flutuante */}
          <g className="fav-coin-badge" filter="url(#fav-shadow)">
            <circle cx="96" cy="84" r="22" fill={GOLD} />
            <text x="96" y="90" textAnchor="middle" fontSize="14" fontWeight="800" fontFamily="Arial, sans-serif" fill="#5a3a06">R$</text>
          </g>
        </g>
      </svg>
    </>
  );
}
